import Vue from 'vue';
import axios from 'axios';
import serialize from 'json-form-data';
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';

const api_url = '/index.php';

function setQueryParams(params) {
    let query = [];

    Object.entries(params).forEach(([key, value]) => {
        if (value) {
            query.push(`&${key}=${value}`);
        }
    });

    return query.join('');
}

const state = () => ({
    typeList: [],
    categories: [],
    positions: [],
    materials: [],
    surveys: [],
    lastChangeWasGroupSorting: false,
});

const mutations = {
    setTypeList(state, typeList) {
        state.typeList = Object.assign([], typeList);
    },
    setCategories(state, categories) {
        state.categories = categories;
    },
    setPostCategories(state, postCategories) {
        state.postCategories = postCategories;
    },
    setPositions(state, positions) {
        state.positions = positions;
    },
    setMaterials(state, materials) {
        state.materials = materials;
    },
    setSurveys(state, surveys) {
        state.surveys = surveys;
    },
    updateMaterialCategory(state, {categoryId, violation}) {
        const group = state.categories.find((g) => g.id === categoryId);
        if (!group) {
            let material = state.materials.find((v) => v.id === violation.id);
            if (material && material.category_id) material.category_id = null;
            state.categories.forEach(category => {
                if (category.items) category.items.filter(
                    item => item.id !== violation.id);
            });

            return;
        } else {
            let findedItem = group.items.find((v) => v.id === violation.id);
            if (findedItem) findedItem.category_id = categoryId;
        }
    },
    setLastChangeWasGroupSortingValue(state, value) {
        state.lastChangeWasGroupSorting = value;
    },
    setMaterialsToCategory(state, {id, items}) {
        const group = state.categories.find((group) => group.id === id);
        group.items = items;
    },
};

const actions = {
    async getTypeList({commit, rootState}) {
        const token = rootState.authToken;

        const {data} = await axios.get(
            addAuthKey(`${api_url}?r=api/educational-material/type-list`,
                token));

        commit('setTypeList', data);
    },
    async createType({rootState}, params) {
        const token = rootState.authToken;

        const formData = serialize(params);

        const {data} = await axios.post(
            addAuthKey(`${api_url}?r=api/educational-material/create-type`,
                token), formData);

        return data;
    },
    async updateType({rootState}, params) {
        const token = rootState.authToken;

        const formData = serialize(params);

        const {data} = await axios.post(addAuthKey(
            `${api_url}?r=api/educational-material/update-type&id=${params.id}`,
            token), formData);

        return data;
    },
    async removeType({rootState}, params) {
        const token = rootState.authToken;

        const {data} = await axios.post(addAuthKey(
            `${api_url}?r=api/educational-material/delete-type&id=${params.id}`,
            token));

        return data;
    },
    async updateTypeSort({rootState, state}, sortedList) {
        const token = rootState.authToken;
        const url = addAuthKey(
            `${api_url}?r=api/educational-material/update-sort`, token);

        const sortingData = [];

        sortedList.forEach((v, vIndex) => {
            sortingData.push({id: v.id, sort: sortedList.length - vIndex});
        });

        await axios.patch(url, sortingData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    },
    async createCategory({rootState}, params) {
        const token = rootState.authToken;

        const formData = serialize(params);

        const {data} = await axios.post(
            addAuthKey(`${api_url}?r=api/educational-material/create-category`,
                token), formData);

        return data;
    },
    async updateCategory({rootState}, params) {
        const token = rootState.authToken;

        const formData = serialize(params);

        const {data} = await axios.post(addAuthKey(
            `${api_url}?r=api/educational-material/update-category&id=${params.id}`,
            token), formData);

        return data;
    },
    async updateCategorySort({rootState, state, commit}) {
        const token = rootState.authToken;
        const url = addAuthKey(
            `addAuthKey(${api_url}?r=api/educational-material/update-sort-category`,
            token);

        const sortingData = state.categories.map((group, groupIndex) => ({
            id: group.id,
            sort: state.categories.length - groupIndex,
        }));

        commit('setLastChangeWasGroupSortingValue', false);

        await axios.patch(url, sortingData);
    },
    async removeCategory({rootState}, id) {
        const token = rootState.authToken;

        const {data} = await axios.post(addAuthKey(
            `${api_url}?r=api/educational-material/delete-category&id=${id}`,
            token));

        return data;
    },
    async getCategories({commit, rootState}, params) {
        const token = rootState.authToken;

        const query = setQueryParams(params);

        const {data} = await axios.get(addAuthKey(
            `${api_url}?r=api/educational-material/categories${query}`, token));

        commit('setCategories', data);
    },
    async getSurveys({commit, rootState}) {
        const token = rootState.authToken;

        const {data} = await axios.get(addAuthKey(
            `${api_url}?r=api/foquz/list`, token));

        commit('setSurveys', data);
    },
    async getPostCategories({commit, rootState}, params = {}) {
        const token = rootState.authToken;

        const query = setQueryParams(params);

        const {data} = await axios.get(addAuthKey(
            `${api_url}?r=api/post-categories/post-categories${query}`, token));

        commit('setPostCategories', data);

        return data;
    },
    async getPositions({commit, rootState}, params = {}) {
        const token = rootState.authToken;

        const query = setQueryParams(params);

        const {data} = await axios.get(
            addAuthKey(`${api_url}?r=api/directory/position${query}`, token));

        if (!params.custom) {
            commit('setPositions', data);
        } else {
            return data;
        }
    },
    async createMaterial({rootState}, {params, files}) {
        const token = rootState.authToken;

        const formData = new FormData();

        formData.append('data', JSON.stringify(params));

        if (files.length) {
            files.forEach(file => {
                formData.append('files[]', file);
            });
        }

        const {data} = await axios.post(
            addAuthKey(`${api_url}?r=api/educational-material/create`, token),
            formData);

        return data;
    },
    async updateMaterial({rootState}, {params, files, oldFiles}) {
        const token = rootState.authToken;

        const formData = new FormData();

        params.oldFiles = oldFiles;
        formData.append('data', JSON.stringify(params));

        if (files.length) {
            files.forEach(file => {
                formData.append('files[]', file);
            });
        }

        const {data} = await axios.post(
            addAuthKey(`${api_url}?r=api/educational-material/edit`, token),
            formData);

        return data;
    },
    async uploadImage({rootState}, blobInfo) {
        const token = rootState.authToken;
        const url = addAuthKey(
            `${api_url}/?r=api/educational-material/upload`,
            token
        );

        const formData = new FormData();
        formData.append('file', blobInfo.blob(), blobInfo.filename());

        const { data } = await axios.post(
            addAuthKey(url, token),
            formData
        );

        return data;
    },
    async changeMaterialCategory({rootState, commit}, {categoryId, violation}) {
        const token = rootState.authToken;
        const url = addAuthKey(`${api_url}?r=api/educational-material/edit`,
            token);

        if (categoryId) {
            violation.category_id = categoryId;
        } else if (violation) {
            violation.category_id = null;
        }

        const formData = new FormData();
        formData.append('data', JSON.stringify(violation));

        await axios.post(url, formData);

        commit('updateMaterialCategory', {categoryId, violation});
    },
    async updateMaterialSort({rootState, state}, data, categoryId) {
        const token = rootState.authToken;
        const url = addAuthKey(
            `${api_url}/?r=api/educational-material/update-sort-material`,
            token);

        let sortingData = [];

        if (data) {
            sortingData = data;
        } else {
            if (!categoryId) {
                state.materials.forEach((v, vIndex) => {
                    sortingData.push(
                        {id: v.id, sort: state.materials.length - vIndex});
                });
            } else {
                const group = state.categories.find(
                    (g) => Number(g.id) === Number(categoryId));
                group.items.forEach((v, vIndex) => {
                    sortingData.push(
                        {id: v.id, sort: group.items.length - vIndex});
                });
            }
        }

        await axios.patch(url, sortingData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
    },
    // async getMaterials({ commit, rootState }, params) {
    //   const token = rootState.authToken;

    //   const query = setQueryParams(params);

    //   let { data } = await axios.get(`${api_url}?r=api/educational-material/index${query}&auth_key=${token}`);

    //   data = data.filter(material => !material.category_id)

    //   commit("setMaterials", data);
    // },

    async getMaterials({rootState, commit}, params) {
        const token = rootState.authToken;

        let queryParams = [];

        queryParams.push(
            `categories=${params.categories && params.categories.length ?
                params.categories.join(',') :
                null}`);

        let query = setQueryParams(params);

        const url = addAuthKey(
            `${api_url}/?r=api/educational-material/index${query}&${queryParams.join(
                '&')}&sort=-sort`, token);

        const {data} = await axios.get(url);

        let firstCategory;

        if (params.categories) {
            [firstCategory] = params.categories;
        }

        if (firstCategory && params.categories.length === 1) {
            commit('setMaterialsToCategory', {items: data, id: firstCategory});
            return;
        }

        data.forEach(material => {
            if (material.conditions && material.conditions.length) {
                material.conditions.forEach(condition => {
                    if (condition.worker_post_groups &&
                        condition.worker_post_groups.length) {
                        condition.worker_post_groups.forEach(group => {
                            group.id = group.code;
                        });
                    }
                });
            }
        });

        commit('setMaterials', data);
    },

    async moveMaterialToArchive({rootState}, params) {
        const token = rootState.authToken;

        const formData = serialize(params);

        let {data} = await axios.post(addAuthKey(
            `${api_url}?r=api/educational-material/move-to-archive&id=${params.id}`,
            token), formData);

        return data;
    },
    async moveMaterialFromArchive({rootState}, params) {
        const token = rootState.authToken;

        const formData = serialize(params);

        let {data} = await axios.post(addAuthKey(
            `${api_url}?r=api/educational-material/move-from-archive&id=${params.id}`,
            token), formData);

        return data;
    },
    async removeMaterial({rootState}, params) {
        const token = rootState.authToken;

        const formData = serialize(params);

        let {data} = await axios.post(
            addAuthKey(`${api_url}?r=api/educational-material/delete-material`,
                token), formData);

        return data;
    },
};

const getters = {};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};